import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  addDoc,
  updateDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import { Contract, ContractStatus, ContractType } from '@/models/contract.model';

const CONTRACTS_COLLECTION = 'contracts';

export class ContractService {
  static async getAllContracts(): Promise<Contract[]> {
    const q = query(
      collection(db, CONTRACTS_COLLECTION),
      orderBy('startDate', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToContract(doc.id, doc.data()));
  }

  static async getContractById(id: string): Promise<Contract | null> {
    const docRef = doc(db, CONTRACTS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    return this.docToContract(docSnap.id, docSnap.data());
  }

  static async getContractsByType(type: ContractType): Promise<Contract[]> {
    const q = query(
      collection(db, CONTRACTS_COLLECTION),
      where('type', '==', type),
      orderBy('startDate', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToContract(doc.id, doc.data()));
  }

  static async getExpiringContracts(daysAhead: number = 90): Promise<Contract[]> {
    const allContracts = await this.getAllContracts();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() + daysAhead);
    
    return allContracts.filter(contract => {
      if (!contract.endDate || !contract.autoRenew) return false;
      return contract.endDate <= cutoffDate && contract.endDate >= new Date();
    });
  }

  static async createContract(contractData: Partial<Contract>): Promise<string> {
    const now = Timestamp.now();
    const data: any = {
      contractNumber: contractData.contractNumber || '',
      title: contractData.title || '',
      type: contractData.type || ContractType.other,
      status: contractData.status || ContractStatus.draft,
      partyName: contractData.partyName || '',
      partyType: contractData.partyType || 'other',
      partyId: contractData.partyId || null,
      startDate: contractData.startDate ? Timestamp.fromDate(contractData.startDate) : now,
      endDate: contractData.endDate ? Timestamp.fromDate(contractData.endDate) : null,
      autoRenew: contractData.autoRenew || false,
      renewalDate: contractData.renewalDate ? Timestamp.fromDate(contractData.renewalDate) : null,
      value: contractData.value || 0,
      currency: contractData.currency || 'USD',
      paymentTerms: contractData.paymentTerms || null,
      terms: contractData.terms || null,
      fileUrl: contractData.fileUrl || null,
      keyDates: contractData.keyDates || [],
      obligations: contractData.obligations || [],
      notes: contractData.notes || null,
      createdBy: contractData.createdBy || '',
      createdAt: now,
      updatedAt: now,
    };
    const docRef = await addDoc(collection(db, CONTRACTS_COLLECTION), data);
    return docRef.id;
  }

  static async updateContract(id: string, updates: Partial<Contract>): Promise<void> {
    const docRef = doc(db, CONTRACTS_COLLECTION, id);
    const updateData: any = { updatedAt: Timestamp.now() };
    
    Object.keys(updates).forEach(key => {
      if (updates[key as keyof Contract] !== undefined) {
        const value = updates[key as keyof Contract];
        if (value instanceof Date) {
          updateData[key] = Timestamp.fromDate(value);
        } else {
          updateData[key] = value;
        }
      }
    });

    await updateDoc(docRef, updateData);
  }

  private static docToContract(id: string, data: any): Contract {
    return {
      id,
      contractNumber: data.contractNumber || '',
      title: data.title || '',
      type: data.type || ContractType.other,
      status: data.status || ContractStatus.draft,
      partyName: data.partyName || '',
      partyType: data.partyType || 'other',
      partyId: data.partyId,
      startDate: data.startDate?.toDate() || new Date(),
      endDate: data.endDate?.toDate(),
      autoRenew: data.autoRenew || false,
      renewalDate: data.renewalDate?.toDate(),
      value: data.value || 0,
      currency: data.currency || 'USD',
      paymentTerms: data.paymentTerms,
      terms: data.terms,
      fileUrl: data.fileUrl,
      keyDates: (data.keyDates || []).map((kd: any) => ({
        ...kd,
        date: kd.date?.toDate() || new Date(),
      })),
      obligations: (data.obligations || []).map((ob: any) => ({
        ...ob,
        dueDate: ob.dueDate?.toDate(),
        completedAt: ob.completedAt?.toDate(),
      })),
      notes: data.notes,
      createdBy: data.createdBy || '',
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    };
  }
}

